/**
 * Database Client for Admin Panel
 * 
 * KISS: The admin panel works almost exclusively through RPCs.
 * Instead of reimplementing the full QueryBuilder (DRY violation),
 * we expose only what the admin panel actually needs:
 *   1. rpc() calls (primary pattern — 95% of admin data access)
 *   2. A lightweight from().select() for simple table reads
 * 
 * For complex queries, use RPCs on the backend instead.
 */

import { apiClient, type ApiResult } from './api-client';

/**
 * Normalize RPC response: handle both array and direct object.
 * (See GEMINI.md — "Respuestas RPC - Array vs Objeto Directo")
 */
export function normalizeRpcResponse<T>(data: T | T[] | null | undefined): T[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
}

// ─── Database Client ────────────────────────────────────────────────────────

export const dbClient = {
    /**
     * Call a PostgreSQL function via RPC.
     * This is the primary data access pattern for the admin panel.
     */
    async rpc<T = unknown>(functionName: string, params?: Record<string, unknown>): Promise<{
        data: T | null;
        error: { message: string; code?: string; status?: number } | null;
    }> {
        const result = await apiClient.rpc<T>(functionName, params);
        return {
            data: result.data ?? null,
            error: result.error ?? null,
        };
    },

    /**
     * Lightweight table reader for simple admin queries.
     * For anything complex, prefer an RPC.
     */
    from(table: string) {
        return {
            select: (columns?: string) => ({
                async execute<T = unknown>(): Promise<{ data: T[] | null; error: any }> {
                    const result = await apiClient.post<T[]>(`/db/${table}`, {
                        table,
                        action: 'select',
                        select: columns,
                    });
                    return {
                        data: result.data ? normalizeRpcResponse(result.data as any) as T[] : null,
                        error: result.error ?? null,
                    };
                },

                eq(column: string, value: unknown) {
                    const filters: Record<string, unknown> = { [column]: value };
                    return {
                        async execute<T = unknown>(): Promise<{ data: T[] | null; error: any }> {
                            const result = await apiClient.post<T[]>(`/db/${table}`, {
                                table,
                                action: 'select',
                                select: columns,
                                filters,
                            });
                            return {
                                data: result.data ? normalizeRpcResponse(result.data as any) as T[] : null,
                                error: result.error ?? null,
                            };
                        },
                    };
                },
            }),
        };
    },
};
